import { useState, useMemo, useEffect } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import {
  Visibility,
  Edit,
  Delete,
  Add,
  AdminPanelSettingsRounded,
  WarningAmber,
  SecurityRounded,
  HistoryRounded,
  ToggleOnRounded,
  ToggleOffRounded
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Switch,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Avatar,
  Stack,
  CircularProgress
} from "@mui/material";
import RolesUpdate from "./roles-update";
import RolesDetail from "./roles-detail";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import moment from "moment";
import Error from "../error";
import useUserPermissions from "../../hooks/useUserPermissions";
import {
  mrtManagementTableCustomActionsOuterSx,
  mrtManagementTablePrimaryActionButtonSx,
  mrtManagementTableToolbarOptions,
} from "../../utils/mrtManagementTableToolbar";

// Roles Management component powered by Material React Table
function Roles() {
  const {
    changeRoleStatus,
    createRole,
    updateRole,
    deleteRole,
    getRole,
    getAllRoles,
  } = useUserPermissions();

  // MRT State Management
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });

  // Dialog & View State
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [update, setUpdate] = useState(false);
  const [selectedRoleName, setSelectedRoleName] = useState(null);
  const [selectedRoleData, setSelectedRoleData] = useState({});

  // Safety Confirmation States
  const [statusConfirm, setStatusConfirm] = useState({ open: false, role: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, role: null });
  const [editConfirm, setEditConfirm] = useState({ open: false, role: null });

  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // Data Fetching Logic (Server-Side)
  const {
    isLoading: rolesDataLoading,
    data: rolesData,
    refetch,
    error: fetchRolesError,
  } = useQuery(
    ["roles", pagination, globalFilter, columnFilters, sorting],
    () => {
      const filterParams = globalFilter
        ? JSON.stringify([{
          column_field: "*",
          operator_value: "contains",
          value: JSON.stringify({
            columns: ["name", "status"],
            value: globalFilter
          })
        }])
        : columnFilters.length > 0
          ? JSON.stringify(columnFilters.map(f => ({
            column_field: f.id,
            operator_value: "contains",
            value: f.value
          })))
          : "[]";

      const sortParams = JSON.stringify(sorting.map(s => ({
        field: s.id,
        sort: s.desc ? "desc" : "asc"
      })));

      return axiosPrivate.get(`/roles`, {
        params: {
          page: pagination.pageIndex,
          per_page: pagination.pageSize,
          sort: sortParams,
          filter: filterParams,
        },
      });
    },
    { refetchOnWindowFocus: false, enabled: !!getAllRoles }
  );

  // Mutations
  const { mutate: toggleStatus, isLoading: isToggling } = useMutation((role) =>
    axiosPrivate.patch(`/roles/${role.name}/status`, { status: role.status === "ACTIVE" ? "INACTIVE" : "ACTIVE" }),
    {
      onSuccess: () => {
        enqueueSnackbar("Role status updated successfully", { variant: "success" });
        setStatusConfirm({ open: false, role: null });
        refetch();
      }
    }
  );

  const { mutate: deleteRoleMutate, isLoading: isDeleting } = useMutation((name) =>
    axiosPrivate.delete(`/roles/${name}`),
    {
      onSuccess: () => {
        enqueueSnackbar("Role deleted successfully", { variant: "success" });
        setDeleteConfirm({ open: false, role: null });
        refetch();
      }
    }
  );

  // Handlers
  const handleView = (role) => {
    setSelectedRoleName(role.name);
    setView(true);
    setOpen(true);
  };

  const handleEditRequest = (role) => {
    setSelectedRoleData(role);
    setEditConfirm({ open: true, role });
  };

  const handleProceedEdit = () => {
    setSelectedRoleName(selectedRoleData.name);
    setUpdate(true);
    setView(false);
    setOpen(true);
    setEditConfirm({ open: false, role: null });
  };

  const handleOpenCreate = () => {
    setUpdate(false);
    setView(false);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setUpdate(false);
    setView(false);
  };

  // Columns Definition
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Access Role",
      size: 250,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: row.original.name === 'super-user' ? 'error.lighter' : 'primary.lighter', color: row.original.name === 'super-user' ? 'error.main' : 'primary.main', borderRadius: 2 }}>
            <AdminPanelSettingsRounded fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 800, color: 'secondary.main' }}>
              {row.original.name}
            </Typography>
            {row.original.name === 'super-user' && (
              <Chip label="System Critical" size="small" sx={{ height: '16px', fontSize: '9px', fontWeight: 900, bgcolor: 'error.lighter', color: 'error.main', mt: 0.2 }} />
            )}
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 150,
      Cell: ({ cell, row }) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            label={cell.getValue()}
            size="small"
            sx={{
              fontWeight: 800,
              fontSize: '11px',
              bgcolor: cell.getValue() === "ACTIVE" ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
              color: cell.getValue() === "ACTIVE" ? '#2e7d32' : '#d32f2f',
            }}
          />
          <Switch
            checked={cell.getValue() === "ACTIVE"}
            onChange={() => setStatusConfirm({ open: true, role: row.original })}
            size="small"
            disabled={!changeRoleStatus || row.original.name === "super-user"}
          />
        </Stack>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      size: 150,
      Cell: ({ cell }) => moment(cell.getValue()).format("MMM D, YYYY"),
    },
  ], [changeRoleStatus]);

  const table = useMaterialReactTable({
    columns,
    data: rolesData?.data?.data || [],
    initialState: { showColumnFilters: false, density: 'comfortable' },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    ...mrtManagementTableToolbarOptions,
    muiToolbarAlertBannerProps: fetchRolesError ? { color: 'error', children: 'Error loading data' } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: rolesData?.data?.meta_data?.total || 0,
    state: { columnFilters, globalFilter, isLoading: rolesDataLoading, pagination, sorting },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Details">
          <IconButton size="small" onClick={() => handleView(row.original)} sx={{ opacity: 0.7 }}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        {updateRole && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditRequest(row.original)} color="primary">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {deleteRole && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, role: row.original })} color="error" disabled={row.original.name === "super-user"}>
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <Box sx={mrtManagementTableCustomActionsOuterSx}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.lighter', display: 'flex', flexShrink: 0 }}>
            <SecurityRounded sx={{ color: 'primary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'secondary.main', lineHeight: 1.2 }}>
              System Roles
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {rolesData?.data?.meta_data?.total || 0} authorization levels
            </Typography>
          </Box>
        </Stack>
        {createRole && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={mrtManagementTablePrimaryActionButtonSx}
          >
            Create Role
          </Button>
        )}
      </Box>
    ),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 5,
        border: "1px solid", borderColor: "divider",
        boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: 'background.default',
        fontWeight: 800,
        color: 'text.secondary',
        fontSize: '12px',
        py: 2,
      },
    },
  });

  return fetchRolesError ? (
    <Error error={fetchRolesError?.response?.data?.error} />
  ) : (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100%' }}>
      <MaterialReactTable table={table} />

      {/* Modals for Detail / Update */}
      <Modal open={open} onClose={handleModalClose}>
        <Box>
          {view ? (
            <RolesDetail handleViewModalClose={handleModalClose} roleName={selectedRoleName} />
          ) : (
            <RolesUpdate handleModalClose={handleModalClose} isNew={!update} roleData={selectedRoleData} refetch={refetch} />
          )}
        </Box>
      </Modal>

      {/* Status Confirmation */}
      <SafetyDialog
        open={statusConfirm.open}
        title={statusConfirm.role?.status === 'ACTIVE' ? "Deactivate Role?" : "Activate Role?"}
        content={`Are you sure you want to change the status of the "${statusConfirm.role?.name}" role? This will affect all assigned users.`}
        onConfirm={() => toggleStatus(statusConfirm.role)}
        onCancel={() => setStatusConfirm({ open: false, role: null })}
        isLoading={isToggling}
        color={statusConfirm.role?.status === 'ACTIVE' ? "error" : "success"}
      />

      {/* Delete Confirmation */}
      <SafetyDialog
        open={deleteConfirm.open}
        title="Delete Role?"
        content={`This action is permanent and will remove "${deleteConfirm.role?.name}" from all users. Proceed?`}
        onConfirm={() => deleteRoleMutate(deleteConfirm.role?.name)}
        onCancel={() => setDeleteConfirm({ open: false, role: null })}
        isLoading={isDeleting}
        color="error"
      />

      {/* Edit Confirmation */}
      <SafetyDialog
        open={editConfirm.open}
        title="Modify Role Configuration?"
        content={`Adjusting the permissions for "${editConfirm.role?.name}" may impact system security. Proceed?`}
        onConfirm={handleProceedEdit}
        onCancel={() => setEditConfirm({ open: false, role: null })}
        color="warning"
      />
    </Box>
  );
}

function SafetyDialog({ open, title, content, onConfirm, onCancel, isLoading, color = "primary" }) {
  return (
    <Dialog
      open={open}
      onClose={() => !isLoading && onCancel()}
      PaperProps={{ sx: { borderRadius: 6, p: 1, maxWidth: '400px' } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: `${color}.lighter`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2
        }}>
          <WarningAmber color={color} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{content}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onCancel} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" color={color} onClick={onConfirm} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default Roles;
